import { createServerFn } from '@tanstack/react-start'
import { notFound } from '@tanstack/react-router'
import { NotPrerenderedError, staticDataMiddleware } from './static-data'
import {
  aboutPage,
  animalCarePage,
  blogPost,
  campaignList,
  campaignPage,
  contentPage,
  coreValues,
  homePage,
  newsItem,
  newsListing,
  openRescues,
  takeActions,
} from './content.server'
import type {
  AboutPage,
  AnimalCarePage,
  BlogEntry,
  CampaignEntry,
  CampaignListItem,
  ContentPage,
  CoreValue,
  HomePage,
  NewsEntry,
  Rescue,
  TakeActionItem,
  WithBody,
} from './types'
import type { NewsPage, NewsType } from '../news/types'

// The loaders are static server functions: at prerender time each call's
// result is written to a JSON file, so client-side navigation works on a
// static host with no server. The data itself is loaded in content.server.ts,
// which only these handlers import: Start cuts the handlers (and with them
// that import) from the client bundle, and import protection fails the build
// if anything else ever pulls it in. Keep this file to server functions,
// re-exported types and helpers that are safe in the browser.
//
// Collections and their frontmatter are documented in docs/content.md.

export type {
  AboutPage,
  AnimalCarePage,
  BlogEntry,
  CampaignEntry,
  CampaignListItem,
  CampaignPetition,
  ContentPage,
  CoreValue,
  HomePage,
  NewsEntry,
  Rescue,
  TakeActionItem,
  WithBody,
} from './types'

// A missing entry is a 404 (thrown from a handler, so prerender and dev show
// the not-found page).
const found = <T>(entry: T | undefined): T => {
  if (entry === undefined) throw notFound()
  return entry
}

export const getBlogPost = createServerFn({ method: 'GET' })
  .validator((slug: string) => slug)
  .middleware([staticDataMiddleware])
  .handler(
    async ({
      data: slug,
    }): Promise<{ post: WithBody<BlogEntry>; others: BlogEntry[] }> =>
      found(blogPost(slug)),
  )

export const listCampaigns = createServerFn({ method: 'GET' })
  .middleware([staticDataMiddleware])
  .handler(async (): Promise<CampaignListItem[]> => campaignList())

export const getCampaign = createServerFn({ method: 'GET' })
  .validator((slug: string) => slug)
  .middleware([staticDataMiddleware])
  .handler(async ({ data: slug }): Promise<WithBody<CampaignEntry>> =>
    found(campaignPage(slug)),
  )

/**
 * One page of the /news listing. Only page 1 of "all" is cached by the static
 * server function (that's what's prerendered); every other page is fetched
 * from the per-page JSON emitted at build time (`data/news/<type>/<n>.json`,
 * see vite.config.ts), so no listing ever ships the whole archive.
 */
export const getNewsPage = createServerFn({ method: 'GET' })
  .validator((input: { type: NewsType; page: number }) => input)
  .middleware([staticDataMiddleware])
  .handler(async ({ data }): Promise<NewsPage> =>
    newsListing(data.type, data.page),
  )

export const getNewsItem = createServerFn({ method: 'GET' })
  .validator((slug: string) => slug)
  .middleware([staticDataMiddleware])
  .handler(async ({ data: slug }): Promise<WithBody<NewsEntry>> =>
    found(newsItem(slug)),
  )

export const getOpenRescues = createServerFn({ method: 'GET' })
  .middleware([staticDataMiddleware])
  .handler(async (): Promise<Rescue[]> => openRescues())

export const getTakeActions = createServerFn({ method: 'GET' })
  .middleware([staticDataMiddleware])
  .handler(async (): Promise<TakeActionItem[]> => takeActions())

export const getHomePage = createServerFn({ method: 'GET' })
  .middleware([staticDataMiddleware])
  .handler(async (): Promise<HomePage> => homePage())

export const getAboutPage = createServerFn({ method: 'GET' })
  .middleware([staticDataMiddleware])
  .handler(async (): Promise<AboutPage> => aboutPage())

export const getCoreValues = createServerFn({ method: 'GET' })
  .middleware([staticDataMiddleware])
  .handler(async (): Promise<CoreValue[]> => coreValues())

export const getAnimalCarePage = createServerFn({ method: 'GET' })
  .middleware([staticDataMiddleware])
  .handler(async (): Promise<AnimalCarePage> => animalCarePage())

export const getContentPage = createServerFn({ method: 'GET' })
  .validator((name: ContentPage) => name)
  .middleware([staticDataMiddleware])
  .handler(async ({ data: name }): Promise<string> => found(contentPage(name)))

/**
 * On the static deploy, a slug that wasn't prerendered has no cached server
 * function result: the CDN answers the client's fetch with the SPA shell, which
 * `staticDataMiddleware` reports as `NotPrerenderedError`. That is a 404; any
 * other failure (network drop, 5xx) stays an error instead of a wrong 404 page.
 */
export function orNotFound<T>(promise: Promise<T>): Promise<T> {
  return promise.catch((error: unknown) => {
    if (error instanceof NotPrerenderedError) throw notFound()
    throw error
  })
}
